import { Component, inject, OnInit } from "@angular/core";
import { PageComponent, PagedArray, Shell, Transfer } from "@tytapp/common";
import { ApiAnnouncement, ApiArticle, ApiPressArticle, ApiProduction, ApiTalent, TalentApi, ApiFranchise, ApiUser } from "@tytapp/api";
import { ActivatedRoute } from "@angular/router";
import { combineLatest } from "rxjs";
import { UserService } from "@tytapp/user";
import { AnnouncementsService, ProductionsService } from "@tytapp/common-ui";
import { PressArticlesService } from "@tytapp/common-ui";
import { HostApi } from '@tytapp/common';
import { BillingService } from '@tytapp/billing';
import { take } from 'rxjs/operators';
import { ArticlesService, FranchisesService } from '@tytapp/articles';

@Component({
    selector: 'app-about',
    templateUrl: './about.component.html',
    styleUrls: ['./about.component.scss']
})
export class AboutComponent extends PageComponent implements OnInit {
    private franchisesService = inject(FranchisesService);
    private talentApi = inject(TalentApi);
    private productionsService = inject(ProductionsService);
    private announcementsService = inject(AnnouncementsService);
    private pressArticlesService = inject(PressArticlesService);
    private userService = inject(UserService);
    private hostApi = inject(HostApi);
    private billing = inject(BillingService);
    private articlesService = inject(ArticlesService);

    get pageComponentId() {
        return 'about';
    }

    @Transfer() section: string = 'about';
    @Transfer() recentEpisodes: ApiProduction[];
    @Transfer() franchise: ApiFranchise;
    @Transfer() person: ApiTalent;
    @Transfer() currentAnnouncement: ApiAnnouncement;
    @Transfer() announcements: PagedArray<ApiAnnouncement>;
    @Transfer() pressArticles: PagedArray<ApiPressArticle>;
    @Transfer() talentArticles: ApiArticle[];
    @Transfer() hostProductions: ApiProduction[];
    @Transfer() hostProductionType = null;
    @Transfer() hostProductionUpdated = false;
    @Transfer() renderTalent = false;
    @Transfer() writerFranchise: ApiFranchise[];

    // Paging
    @Transfer() total: number;
    @Transfer() page: number;
    @Transfer() size = 10;
    @Transfer() announcementsPageSize = 50;

    audienceType: string;

    user: ApiUser;
    isOpenBeta: boolean;

    async init() {
        if (!this.hasServerState) {
            await this.userService.ready;

            this.pageTitle = 'About';
            this.person = null;
            this.hostProductionUpdated = false;

            this.recentEpisodes = await this.productionsService.latestVODs(0, 1);
            this.franchise = await this.aboutAndTalents();


            if (this.franchise)
                this.pageTitle = `About ${this.franchise.name}`;
            this.updateFranchiseSocials();
            this.audienceType = this.userService.audienceType;
        }

        let observer = combineLatest(this.route.params, this.route.queryParams, (params, queries) => ({ params, queries }));
        observer.subscribe(this.update.bind(this));

        this.subscribe(this.userService.userChanged, user => this.user = user);
        this.isOpenBeta = await this.shell.hasFeature('apps.web.nation_open_beta');
    }

    async updateFranchiseSocials() {
        if (!this.franchise)
            return;

        this.franchise.twitter_handle = this.franchise.twitter_handle ? this.franchise.twitter_handle.replace("@", '') : null;
        this.franchise.youtube_url = this.franchise.youtube_url ? this.franchise.youtube_url.replace("https://", '') : null;
        this.franchise.facebook_url = this.franchise.facebook_url ? this.franchise.facebook_url.replace("https://", '') : null;
    }

    get billingEnabled() {
        return this.hostApi.capabilities.includes('web_billing:membership')
            || this.hostApi.capabilities.includes('platform_billing:membership')
    }

    async update({ params, queries }) {

        if (this.hasServerState)
            return;

        this.section = 'loading';
        this.person = null;
        this.currentAnnouncement = null;

        let section = params.section;
        let subID = params.subID;

        if (queries.page) {
            this.page = queries.page;
        } else {
            this.page = 1;
        }

        if (subID) {
            this.head.setCanonicalLink(`/about/${section}/${subID}`);
        } else {
            if (this.page > 1)
                this.head.setCanonicalLink(`/about/${section}`);
            else
                this.head.setCanonicalLink(`/about/${section}?page=${this.page}`);
        }

        if (section) {
            if (section == 'talent') {
                this.pageTitle = 'Talent';
                if (subID) {
                    await this.viewHostDetail(subID);
                }
            }
            if (section == 'announcement') {
                this.pageTitle = 'Announcements';
                this.announcements = await this.announcementsService.all((this.page - 1) * this.announcementsPageSize, this.announcementsPageSize);
                this.total = this.announcements.total;
                if (subID) {
                    let entitled = await this.billing.entitlementChanged.pipe(take(1)).toPromise();
                    try {
                        this.currentAnnouncement = <ApiAnnouncement>await this.announcementsService.get(subID);
                        if (!entitled && this.currentAnnouncement.members_only) {
                            alert(`You must be a member to read this announcement.`);
                            if (this.billingEnabled)
                                this.redirection.go(`/join/membership`, true);
                        }
                    } catch (e) {
                        this.router.navigate(['/about/announcement']);
                    }
                }
            }
            if (section == 'press') {
                this.pageTitle = 'Press';
                this.pressArticles = await this.pressArticlesService.all((this.page - 1) * 50, 50);
                this.total = this.pressArticles.total;
            }
            this.section = section;
        } else {
            this.section = 'about';
        }
    }

    async aboutAndTalents() {
        return await <any>this.franchisesService.get('tyt-network')
            .catch(response => {
                if (response.json) {
                    response = response.json();
                }
                throw response;
            });
    }

    async getCurrentHostById(id: any) {
        return await <any>this.talentApi.get(id).toPromise()
            .catch(response => {
                if (response.json) {
                    response = response.json();
                }
                throw response;
            });
    }

    async switchSection(section: string) {
        this.section = section;
        if (section != 'talent') {
            this.person = null;
        }
        if (section != 'announcement') {
            this.currentAnnouncement = null;
        }
    }

    async viewHostDetail(id: string) {
        try {
            this.person = await this.getCurrentHostById(id);
        } catch (e) {
            this.redirection.go(`/about/talent`);
            return;
        }
        this.renderTalent = true;
        this.person.twitter_handle = this.person.twitter_handle ? this.person.twitter_handle.replace("@", '') : null;
        this.person.facebook_url = this.person.facebook_url ? this.person.facebook_url.replace("https://www.facebook.com", "fb.com") : null;
        this.person.instagram_handle = this.person.instagram_handle ? this.person.instagram_handle.replace("@", "") : null;

        this.hostProductions = await this.productionsService.latestProductionByHost(id, 0, 5);
        if (!this.person || this.person.id != id)
            return;

        this.showHostProductionType();

        if (this.person.primary_role === 'writer') {
            this.talentArticles = await this.talentApi.getTalentArticles(id).toPromise();
        }
    }

    async showHostProductionType() {

        this.hostProductions.forEach((production) => {
            if (production.full_length_vods.length) {
                this.hostProductionType = 'episodes';
            }
        });

        if (this.hostProductionType == null) {
            this.hostProductions.forEach((production) => {
                if (production.full_length_podcasts.length) {
                    this.hostProductionType = 'podcasts';
                }
            });
        }

        this.updateHostProduction();
    }

    async updateHostProduction() {

        let productions = [];

        this.hostProductionUpdated = true;
        if (this.hostProductionType === 'episodes') {
            this.hostProductions.forEach((prod) => {
                if (prod.full_length_vods.length) {
                    productions.push(prod);
                }
            });
        }

        else if (this.hostProductionType === 'podcasts') {
            this.hostProductions.forEach((prod) => {
                if (prod.full_length_podcasts.length) {
                    productions.push(prod);
                }
            });
        }

        this.hostProductions = productions;
    }

    goToPage(page) {
        this.router.navigate([], { queryParams: { page } });
    }

    get shouldShowTalentUser() {
        if(!this.user) return false;
        if (this.isOpenBeta || (!this.isOpenBeta && this.user.can_access_tytnation)) {
            return true;
        }
        return false;
    }

    urlForFranchise(franchise: ApiFranchise) {
        return this.articlesService.getFranchiseUrlSync(franchise);
    }

    urlForAnnouncement(announcement: ApiAnnouncement) {
        return this.announcementsService.getAnnouncementUrlSync(announcement, true);
    }
    awards: Award[] = [
        {
            logo: '/assets/awards/webby.svg',
            logoAlt: 'Webby Awards logo',
            franchiseName: 'The Damage Report',
            name: 'Social Media Presence',
            subline: 'Official Webby Honoree',
            year: '2021',
            width: 723,
            height: 400
        },
        {
            logo: '/assets/awards/shorty.svg',
            logoAlt: 'Shorty Awards logo',
            franchiseName: 'The Young Turks',
            name: 'Audience Honor for Live Event Coverage',
            subline: 'Shorty Awards',
            year: '2021',
            width: 723,
            height: 398
        },
        {
            logo: '/assets/awards/webby.svg',
            logoAlt: 'Webby Awards logo',
            franchiseName: 'The Young Turks',
            name: 'News and Politics: Series and Channel',
            subline: 'Official Webby Honoree',
            year: '2019',
            width: 723,
            height: 398
        },
        {
            logo: '/assets/awards/shorty.svg',
            logoAlt: 'Shorty Awards logo',
            franchiseName: 'The Young Turks',
            name: 'Best in Overall YouTube Presence',
            subline: 'Audience Honor',
            year: '2017',
            width: 723,
            height: 398
        },
        {
            logo: '/assets/awards/webby.svg',
            logoAlt: 'Webby Awards logo',
            franchiseName: 'The Young Turks',
            name: 'Best in Overall YouTube Presence',
            subline: 'Audience Honor',
            year: '2017',
            width: 723,
            height: 398
        },
        {
            logo: '/assets/awards/streamy.svg',
            logoAlt: 'Streamy Awards logo',
            franchiseName: 'The Young Turks',
            name: 'Best News & Cultural Channel',
            subline: null,
            year: '2017',
            width: 723,
            height: 398
        },
        {
            logo: '/assets/awards/aha.svg',
            logoAlt: 'American Humanist Association logo',
            franchiseName: 'The Young Turks',
            name: 'Good Without A God',
            subline: 'Advocating Progressive Values and Equality for Humanists, Atheists, and Freethinkers',
            year: '2017',
            width: 723,
            height: 398
        },
    ];
}


export interface Award {
    franchiseName: string;
    logo: string;
    logoAlt: string;
    url?: string;
    name: string;
    subline: string;
    year: string;
    width: number;
    height: number;
}
